import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { jsPDF } from "jspdf";
import { Download, FileText, ArrowLeft } from "lucide-react";
import { Link } from "wouter";

export default function UserGuide() {
  const [generating, setGenerating] = useState(false);

  const generatePDF = () => {
    setGenerating(true);
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let y = 20;
    
    const addTitle = (text: string, size: number = 18) => {
      doc.setFontSize(size);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 64, 175);
      doc.text(text, margin, y);
      y += size * 0.6;
    };
    
    const addSubtitle = (text: string) => {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(51, 65, 85);
      doc.text(text, margin, y);
      y += 8;
    };
    
    const addText = (text: string, indent: number = 0) => {
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(71, 85, 105);
      const lines = doc.splitTextToSize(text, contentWidth - indent);
      doc.text(lines, margin + indent, y);
      y += lines.length * 5;
    };
    
    const addBullet = (text: string) => {
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(71, 85, 105);
      doc.text("•", margin + 5, y);
      const lines = doc.splitTextToSize(text, contentWidth - 15);
      doc.text(lines, margin + 12, y);
      y += lines.length * 5;
    };
    
    const addSpace = (space: number = 5) => {
      y += space;
    };
    
    const checkPageBreak = (needed: number = 30) => {
      if (y > 270 - needed) {
        doc.addPage();
        y = 20;
      }
    };

    // === COVER PAGE ===
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 297, 'F');
    
    doc.setFontSize(36);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("VEXIS", pageWidth / 2, 80, { align: "center" });
    
    doc.setFontSize(18);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(148, 163, 184);
    doc.text("Agent Dashboard User Guide", pageWidth / 2, 95, { align: "center" });
    
    doc.setFontSize(12);
    doc.setTextColor(100, 116, 139);
    doc.text("Human-First Recruitment Platform", pageWidth / 2, 115, { align: "center" });
    doc.text("LHA Holdings Ltd trading as Vexis Talent Agency", pageWidth / 2, 125, { align: "center" });
    
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.text("Version 1.0 - January 2026", pageWidth / 2, 250, { align: "center" });

    // === TABLE OF CONTENTS ===
    doc.addPage();
    y = 20;
    addTitle("Table of Contents", 24);
    addSpace(10);
    
    const toc = [
      "1. Overview & Getting Started",
      "2. The Pipeline Workflow",
      "3. New Registrations",
      "4. Interview Review",
      "5. Candidate Approval",
      "6. Roles",
      "7. Matching",
      "8. Shortlists",
      "9. Hired / Closed",
      "10. Settings",
      "11. Data Storage Reference"
    ];
    
    toc.forEach(item => {
      addText(item);
      addSpace(2);
    });

    // === SECTION 1: OVERVIEW ===
    doc.addPage();
    y = 20;
    addTitle("1. Overview & Getting Started", 20);
    addSpace(8);
    
    addSubtitle("What is VEXIS?");
    addText("VEXIS is a Recruitment Process Outsourcing (RPO) platform that conducts instant video interviews when candidates apply. The system eliminates bias, verifies resume authenticity, and delivers decision-ready candidate profiles.");
    addSpace(8);
    
    addSubtitle("Your Role as an Agent");
    addText("As a recruitment agent, you are the gatekeeper between candidates and employers. Your responsibilities include:");
    addSpace(3);
    addBullet("Managing new candidate registrations");
    addBullet("Reviewing completed video interviews");
    addBullet("Approving candidates for matching");
    addBullet("Matching candidates to suitable roles");
    addBullet("Creating shortlists and sharing dossiers with clients");
    addBullet("Tracking successful placements");
    addSpace(8);
    
    addSubtitle("Logging In");
    addText("Access the dashboard at /login using your agent credentials. Only approved agents can access the dashboard - there is no public registration.");

    // === SECTION 2: PIPELINE WORKFLOW ===
    doc.addPage();
    y = 20;
    addTitle("2. The Pipeline Workflow", 20);
    addSpace(8);
    
    addSubtitle("Overview");
    addText("VEXIS follows a streamlined pipeline workflow designed for efficiency. Each stage represents one decision point, reducing cognitive load and ensuring consistent candidate processing.");
    addSpace(8);
    
    addSubtitle("The Five Stages");
    addSpace(3);
    addBullet("1. NEW REGISTRATIONS - Candidates who have registered via interview links but haven't completed interviews yet.");
    addBullet("2. INTERVIEW REVIEW - Completed interviews awaiting your review. Watch videos, read transcripts, approve or decline.");
    addBullet("3. CANDIDATE APPROVAL - Approved candidates ready for matching. Add verification notes and prepare for client presentation.");
    addBullet("4. MATCHING & SHORTLISTS - Match approved candidates to roles. Create shortlists and generate dossiers for clients.");
    addBullet("5. HIRED / CLOSED - Successful placements and completed recruitment cycles.");
    addSpace(8);
    
    addSubtitle("One Screen, One Decision");
    addText("Each stage is designed so you can make a single decision per screen. This reduces context-switching and speeds up your workflow.");
    addSpace(8);
    
    addSubtitle("Navigation");
    addText("The sidebar shows your current pipeline position. Work through stages sequentially or jump to any stage as needed.");

    // === SECTION 3: NEW REGISTRATIONS ===
    doc.addPage();
    y = 20;
    addTitle("3. New Registrations", 20);
    addSpace(8);
    
    addSubtitle("Purpose");
    addText("View candidates who have registered but haven't yet completed their video interview. This is your queue for sending interview invites.");
    addSpace(8);
    
    addSubtitle("What You See");
    addBullet("Candidate name and email");
    addBullet("Registration date");
    addBullet("Role they registered for");
    addBullet("Current status (pending interview)");
    addSpace(8);
    
    addSubtitle("Actions");
    addBullet("Send Invite - Generate and send an interview link");
    addBullet("View Details - See candidate registration information");
    addBullet("Remove - Remove candidate from the queue (if not suitable)");
    addSpace(8);
    
    addSubtitle("Workflow Tip");
    addText("Review registrations daily. Candidates who receive invites quickly are more likely to complete interviews.");

    // === SECTION 4: INTERVIEW REVIEW ===
    doc.addPage();
    y = 20;
    addTitle("4. Interview Review", 20);
    addSpace(8);
    
    addSubtitle("Purpose");
    addText("Review completed video interviews. Watch responses, read transcripts, review scores, and make approve/decline decisions.");
    addSpace(8);
    
    addSubtitle("Review Interface");
    addText("Each interview shows:");
    addBullet("Video player - Watch all candidate responses");
    addBullet("Transcript - Read what the candidate said");
    addBullet("Highlights - Key points extracted from responses");
    addBullet("Score breakdown - Role Fit, Competence, Communication, Scenario Handling");
    addSpace(8);
    
    addSubtitle("Making Decisions");
    addBullet("APPROVE - Candidate moves to Approval stage, ready for matching");
    addBullet("DECLINE - Candidate is marked as not suitable, removed from active pipeline");
    addBullet("REQUEST REDO - Ask candidate to re-record specific answers (rare)");
    addSpace(8);
    
    addSubtitle("Scoring Guide");
    addBullet("85+ EXCELLENT - Strong candidate, minimal concerns");
    addBullet("70-84 GOOD - Solid candidate, proceed with confidence");
    addBullet("50-69 POTENTIAL - Review carefully, may have gaps");
    addBullet("Below 50 - Significant concerns, consider declining");

    // === SECTION 5: CANDIDATE APPROVAL ===
    doc.addPage();
    y = 20;
    addTitle("5. Candidate Approval", 20);
    addSpace(8);
    
    addSubtitle("Purpose");
    addText("The approval stage is your quality gate. Only candidates you approve here appear in matching and can be shared with clients.");
    addSpace(8);
    
    addSubtitle("Verification Checklist");
    addBullet("Identity Verified - Confirm candidate identity is genuine");
    addBullet("Right to Work - Confirm legal work authorization");
    addBullet("Authenticity - Confirm resume matches interview responses");
    addSpace(8);
    
    addSubtitle("Adding Notes");
    addText("Use the notes field to record:");
    addBullet("Your assessment of the candidate");
    addBullet("Strengths and areas for development");
    addBullet("Recommended roles or industries");
    addBullet("Any concerns for client discussions");
    addSpace(8);
    
    addSubtitle("Human-Verified Badge");
    addText("When all verification checkboxes are complete, the candidate receives a 'Human-Verified' badge - a trust signal for employers.");

    // === SECTION 6: ROLES ===
    doc.addPage();
    y = 20;
    addTitle("6. Roles", 20);
    addSpace(8);
    
    addSubtitle("Purpose");
    addText("Manage job postings from your clients. Create roles, set requirements, and generate interview invites.");
    addSpace(8);
    
    addSubtitle("Creating a Role");
    addText("Click 'New Role' and provide:");
    addBullet("Title - The job title");
    addBullet("Company - Select the client employer");
    addBullet("Location - Where the job is based");
    addBullet("Requirements - Skills and qualifications needed");
    addBullet("Description - Full job description");
    addSpace(8);
    
    addSubtitle("Generating Invites");
    addText("From any role, click 'Generate Invite' to create an interview link. Options:");
    addBullet("Quick Generate - 5 questions, 72-hour expiry, auto-copied");
    addBullet("Custom - Choose 3-12 questions, set custom expiry");
    addSpace(8);
    
    addSubtitle("Role Status");
    addBullet("ACTIVE - Currently accepting candidates");
    addBullet("CLOSED - No longer accepting applications");
    addBullet("FILLED - Position successfully placed");

    // === SECTION 7: MATCHING ===
    doc.addPage();
    y = 20;
    addTitle("7. Matching", 20);
    addSpace(8);
    
    addSubtitle("Purpose");
    addText("Match approved candidates to suitable roles. The system calculates fit scores based on skills, credentials, and interview performance.");
    addSpace(8);
    
    addSubtitle("Match Scores");
    addBullet("Overall Score - Combined match percentage");
    addBullet("Interview Score - Performance in video interview");
    addBullet("Credential Score - Verified certifications bonus");
    addBullet("Skills Score - Technical skill alignment");
    addSpace(8);
    
    addSubtitle("Quick Match");
    addText("Use Quick Match to batch-process candidates:");
    addBullet("Score 85+ - Add only excellent matches");
    addBullet("Score 70+ - Add good and excellent matches");
    addBullet("Score 50+ - Add all potential matches");
    addSpace(8);
    
    addSubtitle("Manual Matching");
    addText("Click any candidate card to review details. Click 'Add to Shortlist' to include in a role's candidate pool.");

    // === SECTION 8: SHORTLISTS ===
    doc.addPage();
    y = 20;
    addTitle("8. Shortlists", 20);
    addSpace(8);
    
    addSubtitle("Purpose");
    addText("Curated candidate lists for clients. Generate dossiers and share via secure links without requiring client login.");
    addSpace(8);
    
    addSubtitle("Creating Shortlists");
    addText("Shortlists are created automatically when you match candidates to roles. You can also manually add candidates from the Matching screen.");
    addSpace(8);
    
    addSubtitle("Generating Dossiers");
    addText("For each shortlisted candidate, generate a dossier containing:");
    addBullet("Executive summary");
    addBullet("Score breakdown");
    addBullet("Highlights and concerns");
    addBullet("Verification status");
    addSpace(8);
    
    addSubtitle("Sharing with Clients");
    addText("Click 'Share' to generate a secure link. Clients can view candidate dossiers without logging in. Access is controlled by their tier:");
    addBullet("Core RPO - Summary and scores only");
    addBullet("Professional RPO - Name, location, summary");
    addBullet("Enterprise RPO - Full details including contact info");

    // === SECTION 9: HIRED / CLOSED ===
    doc.addPage();
    y = 20;
    addTitle("9. Hired / Closed", 20);
    addSpace(8);
    
    addSubtitle("Purpose");
    addText("Track successful placements and completed recruitment cycles. This is your record of wins and closed positions.");
    addSpace(8);
    
    addSubtitle("What You See");
    addBullet("Total placements - Number of successful hires");
    addBullet("Roles filled - Positions successfully closed");
    addBullet("Recent history - Timeline of placements");
    addSpace(8);
    
    addSubtitle("Marking as Hired");
    addText("When a client confirms a hire, update the candidate status to 'Hired'. This moves them to this section and closes the role if all positions are filled.");
    addSpace(8);
    
    addSubtitle("Closed Roles");
    addText("Roles can be closed for various reasons:");
    addBullet("Filled - All positions successfully placed");
    addBullet("Cancelled - Client cancelled the search");
    addBullet("On Hold - Search paused, may reactivate later");

    // === SECTION 10: SETTINGS ===
    doc.addPage();
    y = 20;
    addTitle("10. Settings", 20);
    addSpace(8);
    
    addSubtitle("Purpose");
    addText("Configure your agent account settings and preferences.");
    addSpace(8);
    
    addSubtitle("Available Settings");
    addBullet("Profile Information - Update your agent name and email");
    addBullet("Password - Change your login password");
    addBullet("Notification Preferences - Control email alerts");
    addSpace(8);
    
    addSubtitle("Logout");
    addText("Click the logout button to securely end your session. You'll be redirected to the login page.");

    // === SECTION 11: DATA STORAGE REFERENCE ===
    doc.addPage();
    y = 20;
    addTitle("11. Data Storage Reference", 20);
    addSpace(8);
    
    addSubtitle("Database Tables (PostgreSQL)");
    addSpace(3);
    addBullet("employers - Client company records");
    addBullet("roles - Job postings with requirements");
    addBullet("candidates - Applicant information and resumes");
    addBullet("applications - Application tracking and status");
    addBullet("interviews - Video interview sessions");
    addBullet("interviewQuestions - Generated questions per role");
    addBullet("interviewResponses - Candidate answers with video paths");
    addBullet("verificationResults - Authenticity analysis");
    addBullet("candidateScores - Scoring breakdown per interview");
    addBullet("dossiers - Agent-curated candidate summaries");
    addBullet("dossierShareTokens - Secure share links for employers");
    addBullet("roleInvites - Interview invitation tokens");
    addBullet("enquiries - Incoming employer/candidate enquiries");
    addBullet("jobDocuments - Uploaded job descriptions");
    addBullet("candidateCredentials - Verified certifications");
    addSpace(8);
    
    addSubtitle("File Storage (Replit Object Storage)");
    addBullet("Interview videos - Stored in private directory, accessed via signed URLs");
    addBullet("Job descriptions - PDF/DOCX files from employer uploads");
    addBullet("Resumes - Candidate document uploads");
    addSpace(8);
    
    addSubtitle("Data Retention");
    addText("All data is retained indefinitely unless manually deleted. Video recordings are stored securely and only accessible by agents - employers cannot view raw video files.");

    // === FOOTER ON LAST PAGE ===
    checkPageBreak(40);
    addSpace(20);
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text("© 2026 Vexis Talent Agency", pageWidth / 2, y, { align: "center" });
    y += 5;
    doc.text("Vexis Talent Agency is a trading name of LHA Holdings Ltd", pageWidth / 2, y, { align: "center" });
    y += 5;
    doc.text("All rights reserved.", pageWidth / 2, y, { align: "center" });

    doc.save("VEXIS_Dashboard_User_Guide.pdf");
    setGenerating(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <Link href="/agent">
          <Button variant="ghost" className="mb-6 text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        <Card className="p-8 bg-slate-900 border-slate-800">
          <div className="text-center mb-8">
            <FileText className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-2">VEXIS Dashboard User Guide</h1>
            <p className="text-slate-400">
              Complete documentation for using the agent dashboard
            </p>
          </div>

          <div className="space-y-6 mb-8">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-lg">What's Included</h3>
              <ul className="text-slate-400 mt-2 space-y-1">
                <li>• Complete guide to all 11 dashboard tabs</li>
                <li>• Explanation of every dropdown and status option</li>
                <li>• How to use edit, save, and delete functions</li>
                <li>• Data storage reference (where everything is saved)</li>
                <li>• Workflow guides and best practices</li>
              </ul>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-lg">Sections Covered</h3>
              <div className="text-slate-400 mt-2 grid grid-cols-2 gap-1">
                <span>1. Overview & Getting Started</span>
                <span>7. Employers Tab</span>
                <span>2. Enquiries Tab</span>
                <span>8. Invites Tab</span>
                <span>3. Pipeline Tab</span>
                <span>9. Scoring Tab</span>
                <span>4. Jobs Tab</span>
                <span>10. Matches Tab</span>
                <span>5. Candidates Tab</span>
                <span>11. Settings Tab</span>
                <span>6. Dossiers Tab</span>
                <span>12. Data Storage Reference</span>
              </div>
            </div>
          </div>

          <Button 
            onClick={generatePDF} 
            disabled={generating}
            className="w-full py-6 text-lg bg-blue-600 hover:bg-blue-700"
            data-testid="button-download-guide"
          >
            {generating ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Generating PDF...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Download className="w-5 h-5" />
                Download User Guide (PDF)
              </div>
            )}
          </Button>

          <p className="text-center text-slate-500 text-sm mt-4">
            PDF will be downloaded to your device
          </p>
        </Card>

        <div className="mt-8 text-center text-slate-600 text-sm">
          <p>© 2026 Vexis Talent Agency</p>
          <p>Vexis Talent Agency is a trading name of LHA Holdings Ltd</p>
        </div>
      </div>
    </div>
  );
}
